import { useState } from "react";
import { useT } from "../../i18n/index.js";
import { abrirArquivo } from "../../lib/open-file.js";
import { trpc } from "../../lib/trpc.js";
import { useModals } from "../common/ModalsProvider.js";

/**
 * CEO 2026-10-29 — PDF e e-mail do avoir (escritório e gerente de chantier).
 * O PDF é gerado pelo Gestion na emissão; avoir antigo gera no 1º clique.
 * Avoir do eDSP mantém o PDF original anexado.
 */
export function CreditNoteDocButtons({
  companyId,
  creditNoteId,
  creditNoteNumber,
}: {
  companyId: number;
  creditNoteId: number;
  creditNoteNumber: string;
}) {
  const t = useT();
  const [emailAberto, setEmailAberto] = useState(false);
  const pdfMut = trpc.customerCreditNotes.pdf.useMutation();
  const btn =
    "rounded-md border border-neutral-700 px-2 py-0.5 text-xs hover:border-lime hover:text-lime disabled:opacity-40";
  return (
    <>
      <button
        type="button"
        className={btn}
        disabled={pdfMut.isPending}
        onClick={() =>
          void abrirArquivo(
            async () => (await pdfMut.mutateAsync({ companyId, id: creditNoteId })).url,
          )
        }
        title={t("creditNoteDoc.pdfTitle")}
        data-testid={`avoir-pdf-${creditNoteId}`}
      >
        📄 {pdfMut.isPending ? "…" : "PDF"}
      </button>
      <button
        type="button"
        className={btn}
        onClick={() => setEmailAberto(true)}
        title={t("creditNoteDoc.emailTitle")}
        data-testid={`avoir-email-${creditNoteId}`}
      >
        📧 {t("creditNoteDoc.emailBtn")}
      </button>
      {emailAberto && (
        <CreditNoteEmailModal
          companyId={companyId}
          creditNoteId={creditNoteId}
          creditNoteNumber={creditNoteNumber}
          onClose={() => setEmailAberto(false)}
        />
      )}
    </>
  );
}

function CreditNoteEmailModal({
  companyId,
  creditNoteId,
  creditNoteNumber,
  onClose,
}: {
  companyId: number;
  creditNoteId: number;
  creditNoteNumber: string;
  onClose: () => void;
}) {
  const t = useT();
  const modals = useModals();
  const defaultsQ = trpc.customerCreditNotes.emailDefaults.useQuery({
    companyId,
    id: creditNoteId,
  });
  const [to, setTo] = useState<string | null>(null);
  const [msg, setMsg] = useState("");
  const email = to ?? defaultsQ.data?.to ?? "";
  const sendMut = trpc.customerCreditNotes.sendEmail.useMutation({
    onSuccess: (r) => {
      void modals.alert({
        title: t("creditNoteDoc.sentTitle"),
        message: `${creditNoteNumber} → ${r.sentTo}`,
      });
      onClose();
    },
  });
  const valido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("creditNoteDoc.emailTitle")}
      data-testid="avoir-email-modal"
    >
      <div
        className="mt-16 w-full max-w-md space-y-3 rounded-lg border p-5 shadow-xl"
        style={{
          background: "var(--surface-card, #ffffff)",
          color: "var(--text-strong, #15203a)",
          borderColor: "var(--border, #dde2e7)",
        }}
      >
        <h2 className="text-base font-bold">
          📧 {t("creditNoteDoc.emailTitle")} {creditNoteNumber}
        </h2>
        {defaultsQ.data?.customerName && (
          <p className="text-xs text-text-muted">
            {defaultsQ.data.customerName}
            {defaultsQ.data.invoiceNumber ? ` · ${defaultsQ.data.invoiceNumber}` : ""}
          </p>
        )}
        <label className="block text-sm">
          {t("creditNoteDoc.toLabel")}
          <input
            type="email"
            value={email}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-700 bg-transparent px-3 py-1.5 text-sm"
            data-testid="avoir-email-to"
          />
        </label>
        <label className="block text-sm">
          {t("creditNoteDoc.messageLabel")}
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={t("creditNoteDoc.messagePlaceholder")}
            className="mt-1 w-full rounded-md border border-neutral-700 bg-transparent px-3 py-1.5 text-sm"
          />
        </label>
        {sendMut.error && <p className="text-xs text-red-500">{sendMut.error.message}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
          >
            {t("creditNoteDoc.cancel")}
          </button>
          <button
            type="button"
            disabled={!valido || sendMut.isPending}
            onClick={() =>
              sendMut.mutate({
                companyId,
                id: creditNoteId,
                recipientEmail: email.trim(),
                ...(msg.trim() ? { customMessage: msg.trim() } : {}),
              })
            }
            className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime disabled:opacity-40"
            data-testid="avoir-email-send"
          >
            {sendMut.isPending ? "…" : t("creditNoteDoc.sendBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
